import React from "react";
import PauseRoundedIcon from "@mui/icons-material/PauseRounded";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import { styled, ToggleButton, ToggleButtonGroup } from "@mui/material";
import { useIntl } from "react-intl";
import { useToast } from "@packsize/toast";

import { updateProductionEnabled } from "@/api/machine-group-api";

interface MachineGroupProductionEnableControlProps {
	readonly disabled?: boolean;
	readonly machineGroupId: string;
	readonly productionEnabled: boolean;
}

const StyledToggleButtonGroup = styled(ToggleButtonGroup)`
	opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
`;

export const MachineGroupProductionEnableControl: React.FC<MachineGroupProductionEnableControlProps> = ({
	disabled,
	machineGroupId,
	productionEnabled,
}) => {
	const intl = useIntl();
	const { toastError } = useToast();

	const handleToggleButtonClicked = async (enable: boolean | null) => {
		if (enable === null || enable === productionEnabled) {
			return;
		}

		try {
			await updateProductionEnabled(machineGroupId, enable);
		} catch {
			toastError({
				message: intl.formatMessage({
					id: "Failed to change machine group production status",
					defaultMessage: "Failed to change machine group production status",
				}),
			});
		}
	};

	return (
		<StyledToggleButtonGroup
			disabled={disabled}
			onChange={(_, value) => handleToggleButtonClicked(value)}
			value={productionEnabled}
			exclusive
		>
			<ToggleButton
				role="button"
				value={false}
				data-testid="machine-group-production-enable-control__button__pause"
				data-pendoid="machine-group-production-enable-control__button__pause"
			>
				<PauseRoundedIcon />
			</ToggleButton>
			<ToggleButton
				role="button"
				value
				data-testid="machine-group-production-enable-control__button__play"
				data-pendoid="machine-group-production-enable-control__button__play"
			>
				<PlayArrowRoundedIcon />
			</ToggleButton>
		</StyledToggleButtonGroup>
	);
};
